import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { sendToGemini } from '../utils/gemini';
import { renderMessage } from '../utils/renderMessage';

function GeminiIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M14 2C14 2 15.8 9.2 22 14C15.8 18.8 14 26 14 26C14 26 12.2 18.8 6 14C12.2 9.2 14 2 14 2Z" fill="currentColor" />
    </svg>
  );
}

function AIAssistantPage() {
  const { isArabic } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSend() {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: 'user', text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const reply = await sendToGemini(next);
      setMessages((prev) => [...prev, { role: 'model', text: reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: isArabic ? `حدث خطأ: ${e.message}` : `Error: ${e.message}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  const dir = isArabic ? 'rtl' : 'ltr';

  return (
    <main className="ai-page inner-page" dir={dir}>
      <div className="container ai-page-container">
        <div className="ai-page-header">
          <GeminiIcon className="ai-page-header-icon" />
          <div>
            <span className="eyebrow">{isArabic ? 'الذكاء الاصطناعي' : 'AI Assistant'}</span>
            <h1 className="ai-page-title">
              {isArabic ? 'مساعد حلول الذكي' : 'Holol AI Assistant'}
            </h1>
          </div>
        </div>

        <div className="ai-page-card">
          <div className="ai-page-messages" ref={messagesRef}>
            {messages.length === 0 && (
              <div className="ai-page-welcome">
                <GeminiIcon className="ai-page-welcome-icon" />
                <h3>{isArabic ? 'كيف يمكنني مساعدتك؟' : 'How can I help you?'}</h3>
                <p>
                  {isArabic
                    ? 'اسألني عن الاستثمار العقاري، خدمات النادي، أو أي موضوع آخر.'
                    : 'Ask me about real estate investment, club services, or anything else.'}
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`ai-page-bubble ai-page-bubble-${m.role}`}>
                {renderMessage(m.text)}
              </div>
            ))}
            {loading && (
              <div className="ai-page-bubble ai-page-bubble-model ai-page-typing">
                <span /><span /><span />
              </div>
            )}
          </div>

          <div className="ai-page-footer" dir={dir}>
            <input
              ref={inputRef}
              className="ai-page-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={isArabic ? 'اكتب رسالتك…' : 'Type a message…'}
              disabled={loading}
              dir={dir}
            />
            <button
              className="primary-btn ai-page-send-btn"
              onClick={handleSend}
              disabled={loading || !input.trim()}
            >
              {isArabic ? 'إرسال' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

export default AIAssistantPage;
